import Modal from "@/common/elements/Modal";
import Button from "@/common/elements/Button";
import AddDetention, { MODE } from "../AddDetention";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import dayjs from "dayjs";
import { hasCheckSecurity, isNonEmptyVariable } from "@/util/common_function";
import { saveExtraTableRecord } from "@/util/extraColumnActions";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { MODULES_LIST_WITH_ID } from "@/util/common_variable";

const EditDetentionsModal = ({
  show,
  onHide,
  formData,
  isEdit = false,
  allDetentionData = [],
}) => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );

  const onModalHide = () => {
    onHide();
  };

  const onSaveChanges = () => {
    const values = formRef.current.getFormValues();
    // DetentionsBioExtra's dynamic fields never flow through values.
    // detentionsBio (DetentionsBioForm splits them out before they reach
    // AddDetention's own form — see its formatPayload) — read separately
    // here and merged onto detentionsBio[0].detentionsBioExtra below.
    const detentionsBioExtraSnapshot =
      formRef.current?.getDetentionsBioExtraSnapshot?.();
    let allDetentionDataCopy = allDetentionData;

    if (isEdit) {
      allDetentionDataCopy = allDetentionDataCopy?.map((ad) => {
        if (ad?.uuid === formData?.uuid) {
          const existingBio = ad?.detentionsBio?.[0] || {};
          const updatedBio = values?.detentionsBio?.[0] || {};
          const existingBioExtra = existingBio?.detentionsBioExtra?.[0] || {};
          const mergedBioExtra = detentionsBioExtraSnapshot
            ? {
                ...existingBioExtra,
                ...detentionsBioExtraSnapshot,
                detentionBioExtraID:
                  existingBioExtra?.detentionBioExtraID ??
                  formRef.current?.detentionsBioExtraID ??
                  0,
                detentionBioID:
                  existingBioExtra?.detentionBioID ??
                  existingBio?.detentionBioID ??
                  0,
              }
            : null;

          // Best-effort immediate save via the generic extra-table
          // endpoint (POST /api/extra/save) — this record already has a
          // real detentionBioID (editing an existing detention, always
          // true for this modal), matching how Charges saves ChargesExtra
          // immediately on its own modal's Save (see
          // EditChargesModal.jsx). The detention record itself keeps its
          // existing save mechanism below (dispatched into Redux, actually
          // persisted when the whole booking is saved) — untouched.
          // Note: the raw API's own PK for a detention's Bio record is
          // "detentionBioID" — NOT "detentionsBioId" as
          // bookingTree.js/detentionTree.js's primaryKey metadata claims
          // (confirmed against a live payload); that mismatch was why
          // this save never found an id to use.
          if (mergedBioExtra && existingBio?.detentionBioID) {
            saveExtraTableRecord({
              tableName: "DetentionsBioExtra",
              idColumn: "detentionBioID",
              idValue: existingBio.detentionBioID,
              extraRecord: mergedBioExtra,
              extraIdKey: "detentionBioExtraID",
            }).catch((error) => {
              console.error(
                "[EditDetentionsModal] DetentionsBioExtra save failed:",
                error,
              );
            });
          }

          return {
            ...ad,
            detentionsBio: [
              {
                ...existingBio,
                ...updatedBio,
                detentionsBioExtra: mergedBioExtra
                  ? [mergedBioExtra]
                  : existingBio?.detentionsBioExtra,
              },
            ],
            detentionsArrestReports: ad?.detentionsArrestReports?.map((a) => {
              return {
                ...a,
                ...values,
                controllingAgencyCaseNumber:
                  isNonEmptyVariable(values?.controllingAgencyCaseNumber) &&
                  !isNaN(values?.controllingAgencyCaseNumber)
                    ? parseInt(values?.controllingAgencyCaseNumber)
                    : null,
                housedFor: values?.housedFor
                  ? dayjs(values?.housedFor)?.format("MM/DD/YYYY")
                  : null,
                incarcerationDate: values?.incarcerationDateTime
                  ? dayjs(values?.incarcerationDateTime)?.format("MM/DD/YYYY")
                  : null,
                incarcerationTime: values?.incarcerationDateTime
                  ? dayjs(values?.incarcerationDateTime)?.format("HH:mm")
                  : null,
                releaseDate: values?.releaseDateTime
                  ? dayjs(values?.releaseDateTime)?.format("MM/DD/YYYY")
                  : null,
                releaseTime: values?.releaseDateTime
                  ? dayjs(values?.releaseDateTime)?.format("HH:mm")
                  : null,
              };
            }),
          };
        } else {
          return ad;
        }
      });
    }

    dispatch(
      setBookings({
        detentions: allDetentionDataCopy,
      })
    );

    onModalHide();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onModalHide}
        header="Detention"
        designPattern="ant"
        width="75%"
        footerActionsNeed
        footerActions={[
          <Button
            key="save"
            type="primary"
            onClick={onSaveChanges}
            disabled={!bookingsFieldsEnabled}
          >
            Save
          </Button>,
          <Button key="cancel" onClick={onModalHide}>
            Cancel
          </Button>,
        ]}
        destroyOnClose
        scrollable
      >
        <AddDetention
          formData={formData}
          mode={MODE.EDIT}
          formRef={formRef}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
          disabled={
            !bookingsFieldsEnabled ||
            !hasCheckSecurity(
              Security_Types.functionality,
              Entity_types.module,
              LABELS_CAPITAL.MODIFY,
              MODULES_LIST_WITH_ID.MODULE_DETENTIONS
            )
          }
        />
      </Modal>
    </>
  );
};

export default EditDetentionsModal;
